import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';

/**
 * FAQ Section Component with Schema.org markup
 * Displays expandable FAQ items with proper SEO structure
 * Note: JSON-LD FAQPage schema is handled by the SEO component
 */
export const FAQSection = ({ faqs = [], title = "Frequently Asked Questions" }) => {
  const [openIndex, setOpenIndex] = useState(null);

  if (!faqs || faqs.length === 0) return null;

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-12" data-testid="faq-section">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-12 h-12 bg-brand-yellow rounded-xl flex items-center justify-center">
          <HelpCircle className="w-6 h-6 text-gray-900" />
        </div>
        <h2 className="text-3xl font-playfair font-bold text-gray-900">{title}</h2>
      </div>

      <div className="space-y-3" itemScope itemType="https://schema.org/FAQPage">
        {faqs.map((faq, index) => (
          <div 
            key={index}
            className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-gray-300 smooth-transition"
            itemScope 
            itemProp="mainEntity" 
            itemType="https://schema.org/Question"
            data-testid={`faq-item-${index}`}
          >
            <button
              onClick={() => toggleFAQ(index)}
              className="w-full px-6 py-5 text-left flex items-center justify-between gap-4 group"
              aria-expanded={openIndex === index}
            >
              <span 
                className="font-semibold text-gray-900 group-hover:text-brand-red smooth-transition pr-4"
                itemProp="name"
              >
                {faq.question}
              </span>
              <ChevronDown 
                className={`w-5 h-5 text-gray-400 flex-shrink-0 smooth-transition ${
                  openIndex === index ? 'rotate-180 text-brand-red' : ''
                }`}
              />
            </button>
            
            <div 
              className={`overflow-hidden smooth-transition ${
                openIndex === index ? 'max-h-96' : 'max-h-0'
              }`}
              itemScope 
              itemProp="acceptedAnswer" 
              itemType="https://schema.org/Answer"
            >
              <div className="px-6 pb-5 pt-0 border-t border-gray-100">
                <p className="text-gray-600 leading-relaxed" itemProp="text">
                  {faq.answer}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default FAQSection;
